import { Network } from '../util';
import { JAccessibilityApi } from './accessibility/accessibility';
import { AdminLicenseApi, AdminLogApi, AdminStoreApi, AdminUtilsApi } from './admin';
import { JAnimationsApi } from './animations/animations';
import { JApplicationApi } from './application';
import { JArticleApi } from './article/article';
import { JAseiApi } from './asei/asei';
import { JAssetReservation } from './assetReservation/assetReservation';
import { JAudienceApi } from './audience/audience';
import { JBbbApi } from './bbb/bbb';
import { JBookmarkApi } from './bookmark/bookmark';
import { JBusinessNetworkingApi } from './businessNetworking/businessNetworking';
import { JCalendarApi } from './calendar/calendar';
import { JCommunityApi } from './community/community';
import { JContentReportApi } from './contentReport/contentReport';
import { JContentFactory } from './contentFactory/contentFactory';
import { JDatasourceApi } from './datasource/datasource';
import { JDiapazoneApi } from './diapazone/diapazone';
import { JDigitalDeskApi } from './digitalDesk/digitalDesk';
import { JDriveApi } from './drive/drive';
import { JExternalAppApi } from './externalApp/externalApp';
import { JExtraBotApi } from './extraBot/extraBot';
import { JFaqApi } from './faq/faq';
import { JFileApi } from './file/file';
import { JFilebankApi } from './filebank/filebank';
import { JFileSyncApi } from './fileSync/fileSync';
import { JGlobalApi } from './global/global';
import { JGroupApi } from './group/group';
import { JIAApi } from './jia/jia';
import { JitsiApi } from './jitsi/jitsi';
import { JLandApi } from './jland/jland';
import { JLuccaApi } from './lucca/lucca';
import { JMagicPadApi } from './magicPad/magicPad';
import { JMediaLibraryApi } from './mediaLibrary/mediaLibrary';
import { JMenuApi } from './menu/menu';
import { JMessengerApi } from './messenger/messenger';
import { JModuleApi } from './module/module';
import { JObjectApi } from './object/object';
import { JOfficeApi } from './office/office';
import { JQuestionAnswerApi } from './questionAnswer/questionAnswer';
import { JSearchApi } from './search/search';
import { JShareApi } from './share/share';
import { JSocialActionApi } from './socialAction/socialAction';
import { JTaxonomyApi } from './taxonomy';
import { TVDisplayApi } from './tvDisplay/tvDisplay';
import { JTwoFactorAuthenticationApi } from './twoFactorAuthentication/twoFactorAuthentication';
import { JUserApi } from './user/user';
import { JVisioConnectApi } from './visioConnect/visioConnect';
import { JWedocApi } from './wedoc/wedoc';
import { JWidgetApi } from './widget/widget';

/**
 * In browser usage, 'window' should be the window object.
 * In server side usage, 'window' object given must implement the fetch method, used by the Apis.
 * See tests implementation for a working Node sample.
 *
 * Each property (except {@link constants} and {@link window}) is an API providing access to a Jamespot.Pro backend.
 */
export class JamespotUserApi {
    public version = '1.0';
    public network: Network;
    public admin: {
        license: AdminLicenseApi;
        store: AdminStoreApi;
        utils: AdminUtilsApi;
        logs: AdminLogApi;
    };
    public accessibility: JAccessibilityApi;
    public application: JApplicationApi;
    public article: JArticleApi;
    public asei: JAseiApi;
    public assetReservation: JAssetReservation;
    public audience: JAudienceApi;
    public bbb: JBbbApi;
    public bookmark: JBookmarkApi;
    public businessNetworking: JBusinessNetworkingApi;
    public calendar: JCalendarApi;
    public community: JCommunityApi;
    public contentReport: JContentReportApi;
    public contentFactory: JContentFactory;
    public datasource: JDatasourceApi;
    public diapazone: JDiapazoneApi;
    public digitalDesk: JDigitalDeskApi;
    public drive: JDriveApi;
    public externalApp: JExternalAppApi;
    public extraBot: JExtraBotApi;
    public faq: JFaqApi;
    public file: JFileApi;
    public filebank: JFilebankApi;
    public fileSync: JFileSyncApi;
    public global: JGlobalApi;
    public group: JGroupApi;
    public jia: JIAApi;
    public jitsi: JitsiApi;
    public jland: JLandApi;
    public lucca: JLuccaApi;
    public magicPad: JMagicPadApi;
    public mediaLibrary: JMediaLibraryApi;
    public menu: JMenuApi;
    public messenger: JMessengerApi;
    public module: JModuleApi;
    public object: JObjectApi;
    public office: JOfficeApi;
    public questionAnswer: JQuestionAnswerApi;
    public search: JSearchApi;
    public share: JShareApi;
    public socialAction: JSocialActionApi;
    public taxonomy: JTaxonomyApi;
    public tvDisplay: TVDisplayApi;
    public twoFactorAuthentication: JTwoFactorAuthenticationApi;
    public user: JUserApi;
    public visioConnect: JVisioConnectApi;
    public wedoc: JWedocApi;
    public widget: JWidgetApi;
    public animations: JAnimationsApi;
    public constructor(network: Network) {
        this.network = network;

        this.admin = {
            license: new AdminLicenseApi(this),
            store: new AdminStoreApi(this),
            utils: new AdminUtilsApi(this),
            logs: new AdminLogApi(this),
        };
        this.accessibility = new JAccessibilityApi(this);
        this.application = new JApplicationApi(this);
        this.article = new JArticleApi(this);
        this.asei = new JAseiApi(this);
        this.assetReservation = new JAssetReservation(this);
        this.audience = new JAudienceApi(this);
        this.bbb = new JBbbApi(this);
        this.bookmark = new JBookmarkApi(this);
        this.businessNetworking = new JBusinessNetworkingApi(this);
        this.calendar = new JCalendarApi(this);
        this.community = new JCommunityApi(this);
        this.contentReport = new JContentReportApi(this);
        this.contentFactory = new JContentFactory(this);
        this.datasource = new JDatasourceApi(this);
        this.diapazone = new JDiapazoneApi(this);
        this.digitalDesk = new JDigitalDeskApi(this);
        this.drive = new JDriveApi(this);
        this.externalApp = new JExternalAppApi(this);
        this.extraBot = new JExtraBotApi(this);
        this.faq = new JFaqApi(this);
        this.file = new JFileApi(this);
        this.filebank = new JFilebankApi(this);
        this.fileSync = new JFileSyncApi(this);
        this.global = new JGlobalApi(this);
        this.group = new JGroupApi(this);
        this.jia = new JIAApi(this);
        this.jitsi = new JitsiApi(this);
        this.jland = new JLandApi(this);
        this.lucca = new JLuccaApi(this);
        this.magicPad = new JMagicPadApi(this);
        this.mediaLibrary = new JMediaLibraryApi(this);
        this.messenger = new JMessengerApi(this);
        this.menu = new JMenuApi(this);
        this.module = new JModuleApi(this);
        this.object = new JObjectApi(this);
        this.office = new JOfficeApi(this);
        this.questionAnswer = new JQuestionAnswerApi(this);
        this.search = new JSearchApi(this);
        this.share = new JShareApi(this);
        this.socialAction = new JSocialActionApi(this);
        this.taxonomy = new JTaxonomyApi(this);
        this.tvDisplay = new TVDisplayApi(this);
        this.twoFactorAuthentication = new JTwoFactorAuthenticationApi(this);
        this.user = new JUserApi(this);
        this.visioConnect = new JVisioConnectApi(this);
        this.wedoc = new JWedocApi(this);
        this.widget = new JWidgetApi(this);
        this.animations = new JAnimationsApi(this);
    }
}
